// @ts-nocheck
/**
 * DialerContext — Shared state for all dialer screens.
 *
 * 2026-06-04 (Kevin) — Migre de .jsx a .tsx pour aligner avec le reste
 * du repo (qui est en TypeScript). @ts-nocheck en haut car le code
 * a 1700 lignes et 100+ acces sans types stricts (refs, any sur Twilio
 * SDK, etc.). Les types publics sont definis dans DialerContext.d.ts
 * et restent applicables (cf. ambient module).
 *
 * Pour retirer @ts-nocheck a terme : passer en strict graduellement,
 * commencer par les setters de state et les actions externes.
 *
 * Extracted from the ParallelDialer monolith so that each VF screen
 * can consume only what it needs via `useDialer()`.
 *
 * Holds: auth / Twilio device / session / queries / handlers / settings.
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { DialerContextValue } from './DialerContextTypes';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/api-client';
import { canCall, dialIntent, sortByDialIntent, CONTACTED_OUTCOMES, ARCHIVED_OUTCOMES, CALLBACK_OUTCOMES, INTERRUPTED_OUTCOME } from '@/services/dialer/dialRules';
import { isQueuedStatus } from '@/services/dialer/prospectStatus';
import { useDialerEvents } from '@/hooks/useDialerEvents';
import { createCoachingService } from './CoachingService';
import { createPageUrl } from '@/utils';
import { toast } from 'sonner';
import { DialerSettingsProvider, useDialerSettings } from './DialerSettingsContext';
import { DialerAuthProvider, useDialerAuth } from './DialerAuthContext';
import { DialerUIProvider, useDialerUI } from './DialerUIContext';
import { DialerGamificationProvider, useDialerGamification } from './DialerGamificationContext';
import { DialerDataProvider, useDialerData } from './DialerDataContext';
import { DialerOrchestrationProvider, useDialerOrchestration } from './DialerOrchestrationContext';
import { TwilioDeviceProvider, useTwilioDevice, useTwilioDeviceInit } from './TwilioDeviceContext';
import { DialerSessionProvider, useDialerSession } from './DialerSessionContext';
import { useDialingMutation, useDialerSessionHandlers, useRollingRefill } from '@/hooks/useDialerSessionHandlers';
import { useShadowCallPool } from '@/state/useShadowCallPool';
import { assertCallbackDated } from '@/services/dialer/callbackSchedule';
import { qualificationNotes } from '@/services/dialer/callNotes';
import { useUnmuteOnNewCall } from '@/hooks/useUnmuteOnNewCall';
import { emitQualificationSaved } from '@/state/callPool';
import { IncomingCallSessionProvider, useIncomingCallSession } from '@/hooks/useIncomingCallSession';
import * as TwilioCalls from '@/services/dialer/TwilioCallService';
import { useDialerDomEvents } from '@/hooks/useDialerDomEvents';
import * as DialerAuth from '@/services/dialer/DialerAuthService';
import { DialerDerivedProvider, useDialerDerived } from './DialerDerivedContext';
import { useDialerCallPoolPorts } from '@/hooks/useDialerCallPoolPorts';
import { DialerTelephonyProvider, useDialerTelephony } from './DialerTelephonyContext';
import { useLocation } from 'react-router-dom';
import { displayListsPoll } from '@/config/polling';

const DialerContext = createContext<DialerContextValue | null>(null);


// ─── Helper ──────────────────────────────────────────────────────────────────

const updateProspectQueueWithRetry = async (id, data, attempt = 0) => {
  if (!id) return; // guard against null/undefined ids
  try {
    return await api.entities.ProspectQueue.update(id, data);
  } catch (e) {
    const isRateLimit = (e?.message && e.message.includes('Rate limit')) || e?.response?.status === 429;
    if (isRateLimit && attempt < 3) {
      const delay = Math.min(400 * Math.pow(2, attempt), 1500);
      await new Promise(r => setTimeout(r, delay));
      return updateProspectQueueWithRetry(id, data, attempt + 1);
    }
    throw e;
  }
};

// ─── Provider ────────────────────────────────────────────────────────────────

function DialerProviderInner({ children }) {
  // Nomme "translate" depuis l'epoque ou useDialerSettings() exposait un `t`
  // maison (dictionnaire dialerTranslations.ts, supprime le 2026-10-08 : plus
  // aucun lecteur). Le nom est garde pour ne pas toucher ses usages.
  const { t: translate } = useTranslation(['dialer']);
  const queryClient = useQueryClient();
  // 2026-06-04 (Fowler 5/3+6/3) — Consomme les sous-contexts au lieu de
  // recevoir les states en props. Le god-context fond progressivement.
  const incomingSession = useIncomingCallSession();
  const { callerData, setCallerData, needsLogin, setNeedsLogin } = useDialerAuth();

  // ── Dialer Session (extrait dans DialerSessionContext, Phase 4 etape 7a) ───
  const session = useDialerSession();
  const {
    isDialing, setIsDialing,
    dialerRunState, setDialerRunState,
    stoppingAll, setStoppingAll,
    dialedPhonesThisSession, setDialedPhonesThisSession,
    isMuted, setIsMuted,
    isOnHold, setIsOnHold,
    dialingSessionStartedAt,
    restartDialingSessionClock,
    stopRequestedRef,
    lastStopAtRef,
    pendingDialsRef,
    autoDialTimerRef,
    lastDialDispatchAtRef,
    dialedPhonesThisSessionRef,
    singleLineEnforceRef,
    previouslyActivePhonesRef,
  } = session;

  // ── Twilio Device (extrait dans TwilioDeviceContext, Phase 4 etape 6) ──────
  const twilioDevice = useTwilioDevice();
  const {
    micPermissionGranted, setMicPermissionGranted,
    micPermissionStatus, retryMicPermission,
    deviceReady, setDeviceReady,
    deviceRef, setDeviceRef,
    deviceState, setDeviceState,
    deviceError, setDeviceError,
    deviceIdentity, setDeviceIdentity,
    incomingCallConnection, setIncomingCallConnection,
    audioInputDevice, setAudioInputDevice,
    audioOutputDevice, setAudioOutputDevice,
    availableDevices, setAvailableDevices,
    deviceInitializedRef,
  } = twilioDevice;

  // ── Settings (extraits dans DialerSettingsContext, Phase 4 etape 5) ────────
  const settings = useDialerSettings();
  const {
    language, setLanguage,
    theme, setTheme, darkMode, cycleTheme,
    soundEnabled, setSoundEnabled,
    powerMode, setPowerMode,
    parallelCallsCount, setParallelCallsCount,
    callerIdRotation, setCallerIdRotation,
    ringTimeout, setRingTimeout,
    maxCallAttempts, setMaxCallAttempts,
    maxCallAttemptsPeriod, setMaxCallAttemptsPeriod,
    powerModePauseBetweenBatches, setPowerModePauseBetweenBatches,
    completeTaskOnDial, setCompleteTaskOnDial,
    powerModeLeaveVoicemail, setPowerModeLeaveVoicemail,
    voicemailAudioUrl, setVoicemailAudioUrl,
    voicemailMode, setVoicemailMode,
    kanbanEnabled, setKanbanEnabled,
    viewMode, setViewMode,
    navTabOrder, setNavTabOrder,
    columnConfig, setColumnConfig,
    selectedMicrophone, setSelectedMicrophone,
    crmProvider, setCrmProvider,
    hubspotSyncConfig, setHubspotSyncConfig,
    hubspotAutoSyncTasks, setHubspotAutoSyncTasks,
    hubspotAutoSyncRecordings, setHubspotAutoSyncRecordings,
    hubspotLastSyncTasks, setHubspotLastSyncTasks,
    hubspotLastSyncRecordings, setHubspotLastSyncRecordings,
    powerHourGoal, setPowerHourGoal,
    stopAfterConnected, setStopAfterConnected,
    personalCallerIds, setPersonalCallerIds,
    powerHourDuration, setPowerHourDuration,
    smartQueueEnabled, setSmartQueueEnabled,
    territories, setTerritories,
    activeMarkets, setActiveMarkets,
    conversationThreshold, setConversationThreshold,
    conversationOutcomes, setConversationOutcomes,
    remindersEnabled, setRemindersEnabled,
    reminderChannels, setReminderChannels,
    aiCoachEnabledPowerMode, setAiCoachEnabledPowerMode,
    aiCoachEnabledManualCall, setAiCoachEnabledManualCall,
  } = settings;

  // ── Auth / caller ──────────────────────────────────────────────────────────
  // callerData et needsLogin sont gerés par le wrapper DialerProvider (cf en bas).
  // CoachingService : singleton dans le provider. Bridge evenementiel entre
  // LiveTranscript (segments SSE prospect) et le panel Live Coach dans CallSheet.
  // Remplace l'ancien polling 8s via window.__lastLiveTranscript.
  const coachingServiceRef = useRef(null);
  if (!coachingServiceRef.current) {
    coachingServiceRef.current = createCoachingService();
  }
  const coachingService = coachingServiceRef.current;

  // Twilio device + audio devices → TwilioDeviceContext (extraits ci-dessus).

  // ── Dialing session ────────────────────────────────────────────────────────
  // isDialing, dialerRunState, stoppingAll, isMuted, isOnHold,
  // dialedPhonesThisSession + 8 refs synchrones → DialerSessionContext.

  // Settings persistes serveur → DialerSettingsContext (Phase 4 etape 5).
  // Les ~30 useState ont ete deplacees + hydration /auth/me + debounce-save.
  // Les valeurs sont destructurees ci-dessus depuis useDialerSettings().

  // twilioNumbers + telephony state — consomme via DialerTelephonyContext.
  // (Fowler etape 8 puis 9 : passe d'un hook prive a un context partage pour
  // que les composants externes puissent y acceder sans la facade useDialer().)
  const telephony = useDialerTelephony();
  const {
    twilioNumbers,
    activeCallerIds, setActiveCallerIds,
    activeCallerNumbers,
    selectedCallerId, setSelectedCallerId,
  } = telephony;

  // ── Gamification (XP / Level) + Power Hour ────────────────────────────────
  // 2026-06-04 (Fowler etape 1/5) — Extrait dans DialerGamificationContext.
  // Voir docs/cartography/dialer-context-dependencies.md §5.
  const gamification = useDialerGamification();
  const {
    agentXP, agentLevel, addXP,
    powerHourActive, setPowerHourActive,
    powerHourTimer, setPowerHourTimer,
    powerHourCalls, setPowerHourCalls,
  } = gamification;

  // smartQueueEnabled — DialerSettingsContext.

  // ── Qualification / email dialogs ──────────────────────────────────────────
  // Phase 4 etape 4d — qualifyingCall (dead state) entierement retire.
  // La qualification post-call passe par CallSheet via l'etat 'qualified'
  // de la machine.
  const [unqualifiedCalls, setUnqualifiedCalls] = useState([]);
  const [prefillMeeting, setPrefillMeeting] = useState(null);
  // Ids of the prospects the contacts screen currently shows. Power Mode only
  // dials inside them (null: no screen published, no restriction).
  const [dialScopeIds, setDialScopeIds] = useState<Set<string> | null>(null);

  // 2026-06-04 (Fowler 4/3) — DELEGATION au DialerUIContext.
  // selectedProspect, selectedProspects, showCallSheet, connectedContact,
  // emailCall, showCelebration, importDialogOpen, showKeypad, dtmfInput,
  // showNotifications sont MAINTENANT dans DialerUIContext (single source
  // of truth). DialerContext continue d'exposer via sa facade pour
  // retrocompat - mais aucune duplication de state.
  const ui = useDialerUI();
  const {
    selectedProspect, setSelectedProspect, selectedProspectRef,
    selectedProspects, setSelectedProspects,
    showCallSheet, setShowCallSheet,
    connectedContact, setConnectedContact, connectedContactRef,
    emailCall, setEmailCall,
    showCelebration, setShowCelebration,
    importDialogOpen, setImportDialogOpen,
    showKeypad, setShowKeypad,
    dtmfInput, setDtmfInput,
    showNotifications, setShowNotifications,
  } = ui;
  // Skip qualification flag (used by handleHangupAndContinue + voicemail_detected handler)
  const skipQualificationRef = useRef(false);

  // ── V5: DTMF Keypad (DialerUIContext, Fowler 4/3) ─────────────────────────
  // showKeypad, dtmfInput delegues a useDialerUI()

  // ── V5: Coach Mode ────────────────────────────────────────────────────────
  const [coachMode, setCoachMode] = useState('off'); // "off" | "listen" | "whisper"

  // ── V5: Celebration Overlay (DialerUIContext) ────────────────────────────
  // showCelebration delegue a useDialerUI()


  // ── V5: Notifications (DialerUIContext partiel) ──────────────────────────
  // showNotifications delegue a useDialerUI(). Liste reste ici (logique metier).
  const [notifications, setNotifications] = useState([]);

  // navTabOrder, columnConfig, crmProvider, hubspotSyncConfig,
  // selectedMicrophone, territories, activeMarkets, conversationThreshold,
  // conversationOutcomes, powerHourDuration, remindersEnabled, reminderChannels,
  // aiCoachEnabledPowerMode, aiCoachEnabledManualCall, hubspotAutoSync*,
  // hubspotLastSync* — tous deplaces dans DialerSettingsContext.

  // ── V5: CRM tab (session state, non persiste) ────────────────────────────
  const [crmTab, setCrmTab] = useState('queue');

  // ── V5: Queue view mode (session state) ──────────────────────────────────
  const [queueViewMode, setQueueViewMode] = useState('kanban');

  // ── Caller ID rotation refs ────────────────────────────────────────────────
  const callerIdIndexRef = useRef(0);
  const callerIdCallCountRef = useRef({});

  // Live durations: useNow() where they are shown (2026-10-07), no clock here.

  // Power Hour countdown timer + toast d'expiration :
  // le countdown est dans DialerGamificationContext (Fowler etape 1/5).
  // On garde ici juste l'effet de bord UI : toast quand le timer revient
  // a 3600 (= bascule active→off automatique cote contexte).
  const prevPowerHourActiveRef = useRef(powerHourActive);
  useEffect(() => {
    if (prevPowerHourActiveRef.current && !powerHourActive) {
      toast.info(translate('dialer:context.powerHourEnded'));
    }
    prevPowerHourActiveRef.current = powerHourActive;
  }, [powerHourActive]);

  // i18next runtime + theme application + hydration /auth/me + debounce-save :
  // tout est gere dans DialerSettingsContext (Phase 4 etape 5).


  // DOM events globaux (Fowler etape 4/5) \u2014 extraits dans useDialerDomEvents.
  // Voir docs/cartography/dialer-context-dependencies.md \u00a75.
  useDialerDomEvents({
    onOpenProspectDetail: (detail) => setSelectedProspect(detail as any),
    onVoicemailDetected: ({ callId, transcript }) => {
      console.log('[Voicemail-STT] received UI event', { callId, transcript });
      skipQualificationRef.current = true;
      setTimeout(() => { skipQualificationRef.current = false; }, 3000);
      setShowCallSheet(false);
      setConnectedContact(null);
      setSelectedProspect(null);
    },
    queryClient,
  });


  // ==========================================================================
  // Auth session hydration + Twilio device init (unchanged from monolith)
  // ==========================================================================

  // Hydrate from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem('revcall_caller');
      if (raw && !callerData) {
        const parsed = JSON.parse(raw);
        if (parsed?.caller_email) setCallerData(parsed);
      }
    } catch (_e) {}
  }, []);

  // Check session + mic permission + fetch numbers + configure webhook
  useEffect(() => {
    const sleep = (ms) => new Promise(r => setTimeout(r, ms));
    const checkSession = async () => {
      for (let i = 0; i < 3; i++) {
        try {
          const res = await api.functions.invoke('callerMe');
          if (res?.status === 200 && res.data?.caller_email) {
            // Marked as coming from the server: the settings provider re-hydrates
            // from it even after hydrating from the (possibly stale) cache.
            setCallerData({ ...res.data, __fromServer: true });
            try { localStorage.setItem('revcall_caller', JSON.stringify(res.data)); } catch (_e) {}
            return;
          }
        } catch (_e) {}
        await sleep(350);
      }
      try {
        const raw = localStorage.getItem('revcall_caller');
        if (raw) { const p = JSON.parse(raw); if (p?.caller_email) { setCallerData(p); return; } }
      } catch (_e) {}
      setNeedsLogin(true);
    };
    checkSession();

    // Mic permission : geree dans TwilioDeviceContext (useEffect mount du
    // provider). DialerContext lit micPermissionStatus + retryMicPermission
    // pour l'UI (banner bloquant + CTA "Autoriser le micro").

    // Twilio numbers list + incoming webhook config — both endpoints are
    // super_admin-only on the backend. Calling them as a regular caller
    // returns 403 SUPER_ADMIN_REQUIRED and pollutes the console with a
    // misleading error. Skip when not super_admin; admin users get the
    // info via the dedicated /TwilioConfig screen anyway.
    // (Moved to a separate effect below so we have access to callerData.)
  }, []);

  // Tenant Twilio numbers + activeCallerIds + activeCallerNumbers +
  // selectedCallerId — TOUT extrait dans useDialerTelephonyNumbers (Fowler 8).

  // Audio devices enumeration → TwilioDeviceContext (Phase 4 etape 6b).

  // Refs late-bound (Fowler etape 3/5) — extraites dans DialerOrchestrationContext.
  // Voir docs/cartography/dialer-context-dependencies.md §4.3.
  const orchestration = useDialerOrchestration();
  const { powerModeRef, dialerRunStateRef, handleStopAllRef,
          syncPowerMode, syncDialerRunState, syncHandleStopAll } = orchestration;
  useEffect(() => { syncPowerMode(!!powerMode); }, [powerMode, syncPowerMode]);
  useEffect(() => { syncDialerRunState(String(dialerRunState || 'idle')); }, [dialerRunState, syncDialerRunState]);
  // Memoire : etait-on en power-mode juste avant de recevoir un appel
  // entrant externe ? Sert au handleRejectIncoming pour relancer le batch.
  // wasPowerModeBeforeIncomingRef retire 2026-06-04 (chantier final 3).
  // Servait a relancer le batch dans handleRejectIncoming - desormais
  // gere par useIncomingCallSession.

  // Twilio Device init → useTwilioDeviceInit (Phase 4 etape 6b).
  // Le hook gere : token fetch + refresh, register, on-events, spawn machine
  // sur incoming, update BDD sur accept/disconnect. Le DialerContext recoit
  // juste le callback onIncomingCallAccepted pour set connectedContact.
  useTwilioDeviceInit({
    callerEmail: callerData?.caller_email,
    onIncomingCallAccepted: ({ enrichedContact }) => {
      setConnectedContact(enrichedContact);
      setShowCallSheet(true);
      // 2026-10-07: the card (ProspectDetailPanel, opened by selectedProspect)
      // opens now, on the server's call:bridged. It used to wait for the
      // 'parallel-calls' refetch to show the call as connected, one network
      // round trip later, before the effect below opened it.
      if (enrichedContact?.callId) {
        // The prospect is in the queue the browser already holds: found by
        // the last 9 digits of the number (the rule of the effect below), the
        // card opens complete instead of waiting for two lookups (2026-10-07).
        const digits = String(enrichedContact.prospect_phone || '').replace(/\D/g, '').slice(-9);
        const known = digits
          ? prospectQueueRef.current.find((p) => p?.prospect_phone && String(p.prospect_phone).replace(/\D/g, '').slice(-9) === digits)
          : null;
        if (known) { setSelectedProspect(known); return; }
        setSelectedProspect({
          id: enrichedContact.prospect_id || '',
          prospect_name: enrichedContact.prospect_name || '',
          prospect_phone: enrichedContact.prospect_phone || '',
          prospect_company: enrichedContact.prospect_company || '',
          prospect_title: enrichedContact.prospect_title || '',
          prospect_email: enrichedContact.prospect_email || '',
          linkedin_url: enrichedContact.linkedin_url || '',
          website: enrichedContact.website || '',
        });
      }
    },
    onIncomingCallDisconnected: (callIdOrSid) => {
      // 2026-06-04 (Kevin demo bug A) — Le SDK Twilio nous passe soit :
      //  - un MongoDB callId (outbound parallel)
      //  - un Twilio CallSid (inbound externe SIP) - prefix 'CA...'
      //  - une string vide (anciens chemins)
      // On accepte les 3 et on resout vers un MongoDB id qu'on peut update.
      const isCallSid = typeof callIdOrSid === 'string' && callIdOrSid.startsWith('CA');
      const currentCallId = connectedContactRef.current?.callId || connectedContactRef.current?.call_id;
      // Resolution du ParallelCall id : si on a un CallSid, on cherche dans
      // activeCalls par external_call_id. Sinon, on prend le callId direct
      // ou le currentCallId comme filet de securite.
      let resolvedId = null;
      if (callIdOrSid && !isCallSid) {
        resolvedId = callIdOrSid;
      } else if (isCallSid) {
        const found = (activeCalls || []).find(c => c.external_call_id === callIdOrSid);
        resolvedId = found?.id || currentCallId || null;
      } else {
        resolvedId = currentCallId || null;
      }

      // GARDE la modale ouverte (selectedProspect intact) pour qualif post-appel.
      // Le pillState basculera a 'ended' automatiquement quand le ParallelCall
      // passe en 'completed' (via React Query refetch).
      // On reset UNIQUEMENT si pas de modale ouverte (cas outbound power mode).
      const hasOpenModal = !!selectedProspectRef.current;
      if (!hasOpenModal) {
        try {
          setConnectedContact(null);
          setShowCallSheet(false);
        } catch (e) { console.warn('[Disconnect] cleanup failed', e); }
      }

      if (resolvedId) {
        api.entities.ParallelCall.update(resolvedId, {
          call_status: 'completed',
          ended_at: new Date().toISOString(),
        }).then(() => {
          queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
        }).catch((e) => {
          console.warn('[Disconnect] ParallelCall.update completed failed for', resolvedId, e?.message);
        });
      } else {
        console.warn('[Disconnect] could not resolve ParallelCall id from', callIdOrSid, '— bouton Raccrocher peut rester affiche');
      }
    },
    onExternalIncomingRinging: (_fromNumber) => {
      // L'agent recoit un appel entrant sur un de ses numeros Twilio. Pour
      // eviter une double-conversation (un outbound qui sonne pourrait
      // decrocher au meme instant), on coupe tout immediatement. Memoire
      // power-mode pour pouvoir relancer un batch si l'agent decline.
      const wasPower = powerModeRef.current === true;
      const wasRunning = dialerRunStateRef.current === 'running';
      if (wasRunning || wasPower) {
        try { handleStopAllRef.current?.(); }
        catch (e) { console.warn('[Inbound] handleStopAll failed on incoming ringing', e); }
      }
    },
  });

  // [migre dans useTwilioDeviceInit]

  // ==========================================================================
  // React Query — data shared across screens
  // ==========================================================================

  // 2026-06-04 (Fowler etape 2/5) — Les 3 queries (prospect-queue, all-calls,
  // parallel-calls) + selectedCustomerId (ADR-024) sont extraites dans
  // DialerDataContext. Voir docs/cartography/dialer-context-dependencies.md §5.
  const data = useDialerData();
  const {
    selectedCustomerId, setSelectedCustomerId,
    prospectQueue, allCalls, activeCalls,
  } = data;
  // Read by the call:bridged callback, registered once with the device.
  const prospectQueueRef = useRef<any[]>([]);
  prospectQueueRef.current = prospectQueue || [];

  // SSE push : les events critiques (voicemail, answered, completed) arrivent en
  // <100ms au lieu d'attendre le poll 500ms. Le poll reste actif comme filet de
  // securite. Sans dependance UI directe — le hook invalide les queries, et les
  // effects existants reagissent comme d'habitude au changement de activeCalls.
  useDialerEvents({
    agentEmail: callerData?.caller_email,
    clientEmail: callerData?.client_email,
    queryClient,
    onEvent: (name, payload) => {
      // 2026-06-02 — Reset connectedContact + close CallSheet quand le call
      // termine cote Twilio (raccrochage par le prospect, AMD, timeout...).
      // Bug Kevin : "le bandeau affiche toujours un appel en cours" alors que
      // Twilio dit completed. Avant, seul disconnect du SDK reseta l'UI ;
      // si le SDK ne propage pas le disconnect (call coupe avant bridge etc),
      // l'UI restait coincee.
      if (name === 'call:completed' || name === 'call:voicemail') {
        try {
          const completedCallId = payload?.callId;
          const currentCallId = connectedContactRef.current?.callId || connectedContactRef.current?.call_id;
          if (!completedCallId || !currentCallId || completedCallId === currentCallId) {
            setConnectedContact(null);
            setShowCallSheet(false);
          }
        } catch (e) { console.warn('[SSE call:completed] cleanup failed', e); }
      }
    },
  });

  // activeCalls — extrait dans DialerDataContext (Fowler etape 2/5).

  // 2026-06-02 (Kevin audit P1.3) — Reset proactif connectedContact +
  // selectedProspect si l'appel a DISPARU d'activeCalls et que ni SSE ni SDK
  // disconnect ne sont arrives. Filet independant : evite que la barre verte
  // et la modale restent affichees indefiniment quand le call est completed
  // cote serveur mais que les signaux client ne sont pas remontes.
  // Debouncing 800ms : evite faux positifs pendant le refetch React Query.
  //
  // 2026-06-03 (Kevin demo) — On NE reset PAS si l'utilisateur a la modale
  // ouverte sur un prospect (selectedProspectRef). Cas typique : le prospect
  // raccroche pendant qu'on lui parle ; la modale doit rester pour la
  // qualification post-appel. Le reset n'arrive que si selectedProspect est
  // vide ou si la modale a deja ete fermee.
  useEffect(() => {
    if (!connectedContact) return;
    const ccId = connectedContact?.callId || connectedContact?.call_id;
    if (!ccId) return;
    const stillActive = (activeCalls || []).some(c => c.id === ccId);
    if (stillActive) return;
    const t = setTimeout(() => {
      const stillActiveNow = (activeCalls || []).some(c => c.id === ccId);
      if (stillActiveNow) return;
      if (connectedContactRef.current && (connectedContactRef.current.callId || connectedContactRef.current.call_id) !== ccId) return;
      // 2026-06-03 - skip si la modale est encore ouverte (selectedProspect set).
      // L'agent doit explicitement fermer ou qualifier pour reset.
      if (selectedProspectRef.current) {
        console.log('[Dialer] proactive reset SKIPPED (modale ouverte sur', selectedProspectRef.current?.prospect_name, ', en attente de qualif)');
        return;
      }
      console.warn('[Dialer] proactive reset: call', ccId, 'disparu d\'activeCalls, reset connectedContact + selectedProspect');
      setConnectedContact(null);
      setShowCallSheet(false);
      setSelectedProspect(null);
    }, 800);
    return () => clearTimeout(t);
  }, [connectedContact, activeCalls, setConnectedContact]);

  // RUI-07 : le micro se rallume au debut de chaque nouvel appel.
  useUnmuteOnNewCall(connectedContact, setIsMuted);

  // Refacto Phase 4 étape 3 — callPool XState avec actions branchées sur les
  // vraies implémentations (toast, hangup Twilio, fetch enrichi, save
  // qualification). CallSheet lit la machine via useActiveCallActor.
  // Console debug : window.__callPool.list() / .getActiveMachine().
  // 2026-05-15 — dedup des sons : si on a joue un beep < 1500ms, skip.
  // Sinon en parallel dial (3 appels), on entendait 3 beeps superposes.
  // De plus, si un humain est deja en ligne (connectedContact set), on
  // freeze tous les sons pour ne pas perturber la conversation.
  // callPoolPorts — extraits dans useDialerCallPoolPorts (Fowler etape 7).
  const callPoolPorts = useDialerCallPoolPorts({
    queryClient,
    activeCalls,
    soundEnabled,
    voicemailMode,
    connectedContactRef,
  });
  const callPool = useShadowCallPool({ activeCalls, ports: callPoolPorts });

  // ADR-031 — TeamMember collection dropped. The "agents" listing now
  // derives from Memberships (consumed directly by the screens that
  // need it via useMembers). Kept as an empty array here for the few
  // consumers that still spread it through context (DialerContext value
  // export below + AnalyticsScreen, which now reads its own source).
  const teamMembers = [];

  // ==========================================================================
  // Computed data
  // ==========================================================================

  // ADR-024 phase 2 — Customer (B2B client of the tenant) selected in the
  // sidebar. null = "Tous les customers" (global view across the tenant) or
  // legacy data without customer_id. Persisted across reloads via localStorage.
  // Declaration MOVED UP near the top of the provider (~line 678) so the
  // queries that scope by customer_id can read it; ESLint dupe guard kept.

  // 2026-06-04 (Fowler etape 6 puis 9) — Vues derivees consommees via
  // DialerDerivedContext (le hook etait jusqu'ici prive a DialerProviderInner).
  const derived = useDialerDerived();
  const {
    filteredProspectQueue, filteredAllCalls, prospectsBySource, sources,
    latestCallByPhone, prospectCallMap, callerIdStats, openFollowupsCount,
    stats, callCountByPhone, isProspectAtCallLimit,
  } = derived;

  // ── Caller ID rotation ─────────────────────────────────────────────────────
  // Picks the next outgoing number from the caller's `activeCallerNumbers`
  // subset (= tenant numbers ∩ Membership.selectedCallerIds, or all when
  // the membership preference is empty).
  const getNextCallerId = useCallback(() => {
    if (callerIdRotation === 'manual' || activeCallerNumbers.length <= 1) return selectedCallerId;
    const numbers = activeCallerNumbers.map(n => n.phoneNumber);
    let nextId;
    if (callerIdRotation === 'round-robin') {
      callerIdIndexRef.current = (callerIdIndexRef.current + 1) % numbers.length;
      nextId = numbers[callerIdIndexRef.current];
    } else {
      nextId = numbers[Math.floor(Math.random() * numbers.length)];
    }
    callerIdCallCountRef.current[nextId] = (callerIdCallCountRef.current[nextId] || 0) + 1;
    setSelectedCallerId(nextId);
    return nextId;
  }, [callerIdRotation, activeCallerNumbers, selectedCallerId, setSelectedCallerId]);

  // ==========================================================================
  // Handlers (exact logic from monolith)
  // ==========================================================================

  const [selectedSource, setSelectedSource] = useState('Revcall Client Dashboard');
  // selectedCustomerId now lives near the top (~ line 800) so its useMemo
  // consumers don't TDZ-crash in Vite's minified prod bundle.

  // 2026-05-21 — si la liste selectionnee disparait (tous ses prospects ont
  // ete supprimes), on bascule sur une source encore presente pour ne pas
  // laisser le selecteur pointer sur une liste fantome.
  useEffect(() => {
    if (sources.length === 0) return;
    if (!sources.includes(selectedSource)) {
      setSelectedSource(sources[0]);
    }
  }, [sources, selectedSource]);

  // Mutation deplacee dans src/hooks/useDialingMutation.js (Phase 4 etape 7b-1).
  const startDialingMutation = useDialingMutation({
    pendingDialsRef, stopRequestedRef, lastStopAtRef, connectedContactRef, dialedPhonesThisSessionRef,
    parallelCallsCount, prospectQueue, filteredProspectQueue, latestCallByPhone,
    isProspectAtCallLimit, selectedSource, selectedCallerId, getNextCallerId, callerData, deviceIdentity,
    deviceReady, dialScopeIds,
    smartQueueEnabled, voicemailMode, voicemailAudioUrl, aiCoachEnabledPowerMode,
    completeTaskOnDial, allCalls, maxCallAttempts, maxCallAttemptsPeriod,
    dialerRunState, setDialerRunState, setPowerMode, setDialedPhonesThisSession,
    powerHourActive, setPowerHourCalls, queryClient,
  });

  // Handlers session deplaces dans src/hooks/useDialerSessionHandlers.js (Phase 4 etape 7b-2).
  const sessionHandlers = useDialerSessionHandlers({
    pendingDialsRef, stopRequestedRef, lastStopAtRef, connectedContactRef,
    dialedPhonesThisSessionRef, autoDialTimerRef, skipQualificationRef,
    prospectQueue, activeCalls, dialerRunState, stoppingAll, powerMode,
    callerData, selectedCustomerId,
    setDialedPhonesThisSession, setDialerRunState, setStoppingAll,
    setPowerMode, setShowCallSheet, setConnectedContact, setSelectedProspect,
    setPowerHourActive, setPowerHourTimer, setPowerHourCalls,
    powerHourActive,
    queryClient,
    startDialingMutation,
    restartDialingSessionClock,
  });
  const { handleStartDialing, handleManualStartDialing, handleResumeDialing, handleStopAll, handleStopDialing,
          handleHangupAndContinue, handleHangupAndNext, handleHangupSingleCall, handleSkipBatch,
          handleTogglePowerHour } = sessionHandlers;

  // Late-bind handleStopAll dans la ref pour le callback inbound ringing.
  useEffect(() => { syncHandleStopAll(handleStopAll); return () => syncHandleStopAll(null); }, [handleStopAll, syncHandleStopAll]);

  // Allow "Relancer" to clear specific phones from the session tracker
  const clearDialedPhonesForRelance = useCallback((phones) => {
    for (const phone of phones) {
      dialedPhonesThisSessionRef.current.delete(phone);
    }
    setDialedPhonesThisSession(Array.from(dialedPhonesThisSessionRef.current));
  }, []);

  // handleLogout — extrait dans DialerAuthService (Fowler etape 5/5).
  const handleLogout = useCallback(() => DialerAuth.logout(), []);

  const handleCallProspect = useCallback(async (prospect) => {
    if (!deviceRef || !deviceReady) { toast.error(translate('dialer:context.deviceNotReady')); return; }
    const existingCall = activeCalls.find(c => c.prospect_phone === prospect.prospect_phone && ['dialing', 'ringing', 'ai_analyzing', 'human_detected', 'connected_to_agent'].includes(c.call_status));
    if (existingCall) { toast.error(translate('dialer:context.callAlreadyInProgress')); return; }
    try {
      toast.info(translate('dialer:context.callingProspect', { name: prospect.prospect_name }));
      // ADR-024 — propagate the prospect's customer_id, OR fall back to the
      // active sidebar selection so manual calls aren't orphaned (always at
      // least one Customer is selected per ADR-024 — see DialerLayout
      // NoCustomersPrompt guard).
      const inheritedCustomerId = prospect.customer_id || selectedCustomerId || '';
      // A single call counts in the Power Mode session too, so the same
      // number is not dialed again by the next batch.
      if (prospect.prospect_phone) dialedPhonesThisSessionRef.current.add(prospect.prospect_phone);
      const call = await api.entities.ParallelCall.create({ customer_id: inheritedCustomerId, prospect_name: prospect.prospect_name, prospect_phone: prospect.prospect_phone, prospect_company: prospect.prospect_company, call_status: 'dialing', agent_email: callerData?.caller_email, agent_name: callerData?.caller_name, started_at: new Date().toISOString() });
      const connection = await deviceRef.connect({ params: { callId: call.id, prospectPhone: prospect.prospect_phone, fromNumber: callerIdRotation !== 'manual' ? getNextCallerId() : selectedCallerId } });
      connection.on('accept', async () => {
        await api.entities.ParallelCall.update(call.id, { external_call_id: connection.parameters.CallSid, call_status: 'connected_to_agent', connected_at: new Date().toISOString() });
        queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
      });
      connection.on('disconnect', async () => {
        try { await api.entities.ParallelCall.update(call.id, { call_status: 'completed', ended_at: new Date().toISOString() }); } catch (_e) {}
        queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
      });
      // ADR-032 — pas d'écriture status='calling'. La présence dans
      // activeCalls (via parallel-calls query) suffit pour piloter l'UI.
      queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
      queryClient.invalidateQueries({ queryKey: ['prospect-queue'] });
    } catch (e) { toast.error('Erreur: ' + (e.message || "Impossible de lancer l'appel")); }
  }, [deviceRef, deviceReady, activeCalls, callerData, callerIdRotation, selectedCallerId, getNextCallerId, queryClient]);

  const qualifyCallMutation = useMutation({
    mutationFn: async ({ callId, qualificationData, isMeeting }) => {
      const call = await api.entities.ParallelCall.update(callId, qualificationData);
      const callData = [...activeCalls, ...(allCalls || [])].find(c => c.id === callId);
      // RUI-06: notes typed in the panel during the call are already stored in
      // call_notes; the qualification payload usually carries none.
      const callNotes = qualificationNotes(qualificationData.call_notes, call?.call_notes ?? callData?.call_notes);
      if (callNotes.length > 10) {
        api.functions.invoke('analyzeCallWithAI', { callId, callNotes, prospectName: callData?.prospect_name, prospectCompany: callData?.prospect_company, outcome: qualificationData.call_outcome }).catch(() => {});
      }
      // RDV : detecte via le flag isMeeting (drapeau meeting_booked), plus
      // via l'outcome qui vaut desormais 'contacted'.
      if (isMeeting) {
        await api.entities.ClientNotification.create({
          notification_id: `meeting_${callId}`,
          title: translate('dialer:context.meetingNotifTitle'),
          message: translate('dialer:context.meetingNotifMessage', {
            name: callData?.prospect_name || translate('dialer:context.meetingNotifDefaultProspect'),
            company: callData?.prospect_company
              ? translate('dialer:context.meetingNotifCompanySuffix', { company: callData.prospect_company })
              : '',
            notes: callNotes,
          }),
          type: 'success',
          is_read: false,
        });
      }
      // ADR-036: Swoft is fed server-side. The PATCH above writes the
      // swoft_outbox row; the old sendProspectToSwoft call (and its
      // swallowed failures) is gone.
      // ── HubSpot sync: push enabled items immediately after qualification ──
      const hsConfig = hubspotSyncConfig;
      const hasAnyEnabled = hsConfig && Object.values(hsConfig).some(Boolean);
      if (hasAnyEnabled && callData) {
        const syncPayload = {
          id: callId,
          phone: callData.prospect_phone,
          prospectName: callData.prospect_name,
          qualification: {
            outcome: qualificationData.call_outcome,
            notes: hsConfig.notes ? callNotes : '',
            meetingDate: hsConfig.meetingLinks ? (qualificationData.meeting_date || '') : '',
            meetingTime: '',
          },
          recordingUrl: hsConfig.recordings ? (callData.recording_url || '') : undefined,
          transcription: hsConfig.transcripts ? (callData.transcription || '') : undefined,
          aiSummary: hsConfig.aiSummary ? (callData.ai_summary || '') : undefined,
          duration: callData.duration_seconds,
          startTime: callData.started_at,
          endTime: callData.ended_at || new Date().toISOString(),
        };
        // Determine sync type based on enabled toggles
        const wantTasks = hsConfig.tasks || hsConfig.notes;
        const wantRecordings = hsConfig.recordings || hsConfig.transcripts || hsConfig.aiSummary;
        const syncType = (wantTasks && wantRecordings) ? 'all' : wantTasks ? 'tasks' : wantRecordings ? 'recordings' : null;
        if (syncType) {
          api.functions.invoke('syncToHubSpot', {
            clientEmail: callerData?.client_email,
            type: syncType,
            calls: [syncPayload],
          }).then((res) => {
            // Stamp only a real write: the route answers 200 even when it
            // skipped (HubSpot not connected), and the "Envoyer au CRM" button
            // reads this stamp as "already sent".
            const out = (res as { data?: { success?: boolean; syncedCount?: number; syncedIds?: Array<{ callId: string; hubspotTaskId: string }> } } | undefined)?.data;
            if (out?.success && (out.syncedCount ?? 0) > 0) {
              // The task id lets the follow-ups screen complete or move the
              // HubSpot task later; the server stores it since AUDIT-F14b.
              const taskId = out.syncedIds?.find((s) => s.callId === callId)?.hubspotTaskId;
              api.entities.ParallelCall.update(callId, {
                hubspot_synced_at: new Date().toISOString(),
                ...(taskId ? { hubspot_task_id: taskId } : {}),
              }).catch(() => {});
              queryClient.invalidateQueries({ queryKey: ['crm-send-status', callId] });
            }
          }).catch((err) => {
            console.warn('[HubSpot sync] Post-qualification sync failed:', err.message || err);
          });
        }
        // Sync meeting outcome separately if enabled
        if (hsConfig.meetingLinks && qualificationData.call_outcome === 'meeting_booked' && callData.prospect_phone) {
          api.functions.invoke('hubspotSyncMeetingOutcome', {
            phone: callData.prospect_phone,
            outcome: 'meeting_booked',
            meetingDate: qualificationData.meeting_date || '',
          }).catch(() => {});
        }
      }
      // ADR-032 — plus de mutation de prospect.status après qualification.
      // Le call_outcome du ParallelCall (déjà écrit ci-dessus) suffit : il
      // pilote dialIntent() qui pilote le badge ET canCall(). prospect.status
      // reste 'active' tout le long de la vie du prospect (sauf DNC explicite).
      return call;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
      queryClient.invalidateQueries({ queryKey: ['all-calls'] });
      queryClient.invalidateQueries({ queryKey: ['prospect-queue'] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-callbacks', callerData?.client_email] });
      // Toast deplace dans CallSheet.handleDisposition (plus informatif :
      // 'Statut enregistré : <label>'). Eviter le doublon.
    },
  });

  const handleQualifyCall = useCallback(async (callId, qualificationData) => {
    // 2026-05-21 — RDV booké = drapeau séparé. Si l'appelant envoie encore
    // l'ancien outcome 'meeting_booked', on le normalise : flag meeting_booked
    // + vrai outcome métier 'contacted'. isMeeting reste vrai pour tous les
    // effets (notif, Swoft, prefill RDV, célébration).
    const isMeeting = qualificationData?.call_outcome === 'meeting_booked' || qualificationData?.meeting_booked === true;
    const normalized = { ...qualificationData };
    // RUI-05: a callback without a date never reached Tâches. Refuse it here,
    // the single entry point of every qualification.
    assertCallbackDated(normalized);
    if (isMeeting) {
      normalized.meeting_booked = true;
      if (normalized.call_outcome === 'meeting_booked') normalized.call_outcome = 'contacted';
    }
    await qualifyCallMutation.mutateAsync({ callId, qualificationData: normalized, isMeeting });
    // RUI-P3 : qualification persistee -> la machine quitte 'qualified'.
    emitQualificationSaved(callPool, callId, normalized);
    try { window.dataLayer && window.dataLayer.push({ event: 'Qualify', outcome: isMeeting ? 'meeting_booked' : (normalized?.call_outcome || 'unknown') }); } catch (_e) {}
    setUnqualifiedCalls(prev => prev.filter(c => c.id !== callId));
    if (isMeeting) {
      const call = allCalls.find(c => c.id === callId) || activeCalls.find(c => c.id === callId);
      if (call) setPrefillMeeting({ prospect_name: call.prospect_name, prospect_phone: call.prospect_phone, prospect_company: call.prospect_company });
      // 2026-05-21 — célébration RDV déclenchée ici (était dans CallSheet,
      // supprimé). Centralisée pour tout chemin de qualification RDV.
      setShowCelebration({ name: call?.prospect_name, company: call?.prospect_company });
    }
  }, [allCalls, activeCalls, qualifyCallMutation, callPool]);

  // 2026-06-04 (refactor big-bang) — handleAcceptIncoming et handleRejectIncoming
  // ont ete deplaces dans src/hooks/useIncomingCallSession.tsx. Les 115 lignes
  // ici ont ete supprimees. Le cycle inbound est maintenant pilote par un
  // useReducer unique avec phases idle/ringing/accepted/live/ended.
  // Consommateurs : DialerLayout (toast + preview) appellent
  // incomingSession.handleAccept / handleReject directement.

  // ── Auto-open / switch CallSheet selon l'etat des appels actifs ─────────
  // Regle : la CallSheet s'ouvre des que Twilio confirme un decroche
  // (call_status = 'connected_to_agent', soit ~0s apres le pickup) — pas
  // besoin d'attendre la classification AMD (~4s plus tard). Le pill au top
  // de la modale affiche "Verification..." pendant la phase AMD, puis bascule
  // sur "En ligne" (humain) ou "Messagerie" (repondeur). Sur repondeur, un
  // autre effect plus bas (auto-close-on-voicemail) ferme la modale apres
  // un court delai pour laisser le caller voir ce qui s'est passe.
  useEffect(() => {
    if (skipQualificationRef.current) return;

    // 2026-06-04 (Kevin demo bug B) — Bail out si on vient de stopper
    // (< 5s). Sans ca, React Query peut refetch un call encore en
    // 'connected_to_agent' (server lag avant que le webhook completed
    // arrive) -> ce useEffect repose connectedContact -> la modale
    // revient juste apres le toast "Appels arretes".
    if (lastStopAtRef.current && Date.now() - lastStopAtRef.current < 5000) return;

    // 2026-06-04 (Fowler audit, supprime le hack window.__incomingCallActive)
    // Bail out si une session inbound est active. Le IncomingCallBridge
    // gere deja la pose de selectedProspect et l'ouverture de la modale
    // pour l'inbound. Si on laisse ce useEffect tirer, il repose
    // connectedContact avec target.prospect_name d'un ParallelCall qui
    // peut ne pas matcher la session en cours (course entre create
    // ParallelCall et refetch React Query).
    if (incomingSession.isLive || incomingSession.isRinging) return;

    // On prefere le call avec un humain confirme (connected_at) s'il y en a
    // un — c'est le plus interessant a afficher. Sinon, n'importe quel
    // 'connected_to_agent' fait l'affaire pour ouvrir la modale en mode
    // 'verifying'.
    // 2026-06-03 (Kevin live demo bug #1) — Si plusieurs candidats, preferer
    // ceux avec un VRAI nom (pas 'Appelant inconnu'). Cas typique : un
    // ParallelCall historique reste en 'connected_to_agent' jamais cloture
    // avec un nom vide -> ecrasait l'inbound courant.
    const isUsableName = (n) => n && String(n).trim() && String(n).trim() !== 'Appelant inconnu';
    const humanCalls = activeCalls.filter(c => c.call_status === 'connected_to_agent' && c.connected_at);
    const verifyingCalls = activeCalls.filter(c => c.call_status === 'connected_to_agent');
    const humanCall = humanCalls.find(c => isUsableName(c.prospect_name)) || humanCalls[0];
    const verifyingCall = verifyingCalls.find(c => isUsableName(c.prospect_name)) || verifyingCalls[0];
    const target = humanCall || verifyingCall;

    if (!target) return; // pas de call actif → on ne touche a rien (mode post-call gere par l'auto-qualify effect)

    // 2026-05-14 — refait le merge tant qu'il manque DES infos enrichies (nom,
    // entreprise OU linkedin). Avant : on s'arretait des qu'on avait un nom →
    // l'agent voyait "v1 nom seul" au pickup, puis flash "v2 nom + entreprise +
    // linkedin" juste avant fermeture. On veut "v2 directement".
    const hasName = !!(connectedContact?.prospect_name || connectedContact?.prospectName);
    const hasCompany = !!(connectedContact?.prospect_company || connectedContact?.prospectCompany);
    const hasLinkedin = !!(connectedContact?.linkedin_url || connectedContact?.linkedinUrl);
    const fullyEnriched = hasName && hasCompany && hasLinkedin;
    if (connectedContact?.callId === target.id && fullyEnriched) return;

    // Si on affiche un autre call qui n'est plus actif → switcher
    // Ou si on a un humain confirme et qu'on affichait un autre dial → switcher
    // 2026-05-13 — on merge les 2 sources : prospectQueue (enrichi) PLUS le
    // ParallelCall lui-meme (qui a au moins prospect_name/phone/company poses
    // a la creation). Garantit que prospect_name est TOUJOURS present, evitant
    // le fallback "Contact" affiche sur la modale.
    // 2026-06-03 (Kevin live demo bug #1) — Match strict par prospect_phone
    // ratait si formats differents (+33699716823 vs 0699716823 etc).
    // Maintenant : compare sur les 9 derniers chiffres digits-only.
    const targetPhoneDigits = String(target.prospect_phone || '').replace(/\D/g, '').slice(-9);
    const prospectFromQueue = targetPhoneDigits
      ? prospectQueue.find(p => p.prospect_phone && String(p.prospect_phone).replace(/\D/g, '').slice(-9) === targetPhoneDigits)
      : null;
    // 2026-05-14 — fix definitif "Contact" : on construit un objet ou les
    // valeurs vides du prospectQueue ne peuvent JAMAIS ecraser les valeurs
    // non-vides du ParallelCall. Avant : spread (...prospectFromQueue) ecrasait
    // prospect_name='Inès Boucher' du target par prospect_name='' du queue,
    // produisant le fallback "Contact".
    const pick = (a, b) => (a && String(a).trim() ? a : (b && String(b).trim() ? b : ''));
    // ── Boucle de rendu — correctif ───────────────────────────────────────
    // Cet effet depend de `connectedContact` ET le reecrivait avec un objet
    // LITTERAL NEUF a chaque passage. Un objet neuf n'est jamais
    // referentiellement egal au precedent : chaque execution reprogrammait le
    // meme effet -> "Maximum update depth exceeded".
    //
    // Le garde `callId === target.id && fullyEnriched` plus haut ne suffisait
    // pas : il ne sort QUE si le contact est completement enrichi (nom ET
    // entreprise ET linkedin). Sur un prospect sans linkedin -- cas ordinaire
    // -- `fullyEnriched` reste faux pour toujours et la boucle ne s'arretait
    // jamais. D'ou l'intermittence : elle ne partait que sur ces prospects-la.
    //
    // On construit la valeur, on la COMPARE, et on n'ecrit que si elle a
    // change. L'intention d'origine ("refait le merge tant qu'il manque des
    // infos enrichies") est preservee : le merge se refait quand
    // `prospectQueue` apporte du nouveau, et s'arrete quand il ne produit plus
    // rien de different -- au lieu de s'arreter seulement quand il produit un
    // resultat complet.
    const contactSuivant = {
      prospect_id: prospectFromQueue?.id || target.prospect_id || '',
      prospect_name: pick(prospectFromQueue?.prospect_name, target.prospect_name),
      prospect_phone: pick(prospectFromQueue?.prospect_phone, target.prospect_phone),
      prospect_company: pick(prospectFromQueue?.prospect_company, target.prospect_company),
      prospect_title: pick(prospectFromQueue?.prospect_title, target.prospect_title),
      prospect_email: pick(prospectFromQueue?.prospect_email, target.prospect_email),
      linkedin_url: pick(prospectFromQueue?.linkedin_url, target.linkedin_url),
      website: pick(prospectFromQueue?.website, target.website),
      notes: pick(prospectFromQueue?.notes, target.notes),
      callId: target.id,
      callStartedAt: target.connected_at || target.started_at,
    };

    // Comparaison sur LES SEULES CLES construites ici : si un autre producteur
    // a pose des cles supplementaires sur `connectedContact`, ne pas ecrire les
    // preserve — alors qu'ecrire les effacerait.
    const contactInchange = !!connectedContact
      && Object.keys(contactSuivant).every((k) => connectedContact[k] === contactSuivant[k]);

    // Rien n'a change : ni l'etat, ni la modale. On sort, et la boucle meurt.
    if (contactInchange) return;

    setConnectedContact(contactSuivant);
    // 2026-05-21 — Modale unifiée : on n'ouvre plus la CallSheet séparée.
    // On ouvre la modale ProspectDetailPanel sur le prospet en cours ; elle
    // détecte l'appel via connectedContact et affiche la card "appel en cours"
    // (transcript + coach) en haut. On préfère l'objet enrichi du queue, sinon
    // on construit un objet prospet minimal depuis le ParallelCall.
    const prospectForModal = prospectFromQueue || {
      id: target.prospect_id || '',
      prospect_name: target.prospect_name || '',
      prospect_phone: target.prospect_phone || '',
      prospect_company: target.prospect_company || '',
      prospect_title: target.prospect_title || '',
      prospect_email: target.prospect_email || '',
      linkedin_url: target.linkedin_url || '',
      website: target.website || '',
    };
    setSelectedProspect(prospectForModal);
    setShowCallSheet(false);
  }, [activeCalls, prospectQueue, connectedContact]);

  // ── Auto-qualify after call ends ───────────────────────────────────────────
  // singleLineEnforceRef → DialerSessionContext.
  const prevActiveCalls = useRef([]);

  useEffect(() => {
    const currentCount = activeCalls.length;
    const prevCount = prevActiveCalls.current.length;
    // 2026-05-14 — la modale doit aussi se fermer (ou basculer en qualification)
    // quand le call AFFICHE se termine, meme si d'autres lignes sont encore
    // actives en Power Mode. Avant : on attendait que TOUS les calls se terminent
    // (prevCount > 0 && currentCount === 0), donc la modale d'un appel raccroche
    // restait visible tant que les autres lignes sonnaient.
    // 2026-05-14 — un call est "ended" pour la modale soit :
    //  (a) il a disparu de activeCalls (refetch query)
    //  (b) il est toujours dans activeCalls mais son status est terminal pour
    //      l'UI : voicemail (repondeur detecte, auto-hangup en cours), ou un
    //      status hors PROSPECT_UI_ACTIVE_STATUSES.
    // Sans (b), la modale Ines Boucher restait ouverte quand notre STT custom
    // detectait un repondeur (call_status='voicemail' present dans activeCalls).
    const TERMINAL_FOR_MODAL = ['voicemail', 'completed', 'failed', 'canceled', 'no_answer'];
    const shownCallId = connectedContact?.callId;
    const shownCallCurrent = shownCallId ? activeCalls.find(c => c.id === shownCallId) : null;
    const shownCallEnded = shownCallId && (
      (prevActiveCalls.current.some(c => c.id === shownCallId) && !shownCallCurrent)
      || (shownCallCurrent && TERMINAL_FOR_MODAL.includes(shownCallCurrent.call_status))
    );
    if ((prevCount > 0 && currentCount === 0) || shownCallEnded) {
      // Skip qualification if "Raccrocher et continuer" was used
      if (skipQualificationRef.current) {
        setConnectedContact(null);
      } else {
        // Discriminant "humain atteint" : on accepte 3 sources de verite, dans
        // l'ordre de fiabilite decroissant :
        //  (1) connected_at set par le webhook AMD answered=human (canonique)
        //  (2) call_status === 'connected_to_agent' avec une duree > 3s — le
        //      bridge a tenu, donc humain probable. Couvre les calls courts
        //      ou l'agent raccroche AVANT que l'AMD asyncAmd ait rendu son
        //      verdict (peut prendre jusqu'a 30s).
        //  (3) outcome humain explicitement saisi (contacted, interested,
        //      meeting_booked, callback, not_interested) — l'agent l'a
        //      qualifie, donc humain par definition.
        const HUMAN_OUTCOMES = ['contacted', 'interested', 'meeting_booked', 'callback_requested', 'not_interested'];
        // 2026-05-13 — exclut explicitement les outcomes "non humain" pour
        // eviter de ré-ouvrir la qualification post-call sur un repondeur
        // detecte par Custom STT (call_status passe a 'voicemail', outcome
        // pose a 'no_answer'). Sans cette garde, c.connected_at (posé sur
        // 'in-progress') re-déclencherait la modale.
        const NON_HUMAN_OUTCOMES = ['no_answer', 'voicemail', 'left_voicemail', 'not_reachable', 'busy', 'failed', 'canceled'];
        // Si la modale affiche un call termine specifique, on regarde CE call.
        // Sinon (cas all-ended), on cherche un humain parmi les calls precedents.
        const isHuman = (c) => {
          if (c.call_outcome && NON_HUMAN_OUTCOMES.includes(c.call_outcome)) return false;
          if (c.call_status === 'voicemail') return false;
          // 2026-05-14 — bug Lara Vogel : AMD pose connected_at sur 'in-progress'
          // mais detecte ensuite machine_end_other. Si amd_result est explicitement
          // machine, on rejette malgre le connected_at.
          if (typeof c.amd_result === 'string' && c.amd_result.startsWith('machine')) return false;
          if (typeof c.amdResult === 'string' && c.amdResult.startsWith('machine')) return false;
          if (c.connected_at) return true;
          if (c.call_status === 'connected_to_agent' && Number(c.duration_seconds || 0) > 3) return true;
          if (c.call_outcome && HUMAN_OUTCOMES.includes(c.call_outcome)) return true;
          return false;
        };
        const shownCallSnapshot = shownCallEnded
          ? (shownCallCurrent || prevActiveCalls.current.find(c => c.id === shownCallId))
          : null;
        // 2026-05-14 — distinction nette :
        //  - shown call est un VRAI voicemail (status='voicemail' OU outcome
        //    non-humain explicite) → on ferme la modale sans qualif, passage
        //    auto au suivant en Power Mode.
        //  - shown call est humain (connected_at set, ou dur > 3s, ou
        //    outcome humain) → ajoute le flag callEnded sur connectedContact
        //    pour que CallSheet bascule sur "Valider et continuer".
        //  - sinon (call non shown qui se termine), chercher humain ailleurs.
        const shownIsVoicemail = shownCallSnapshot && (
          shownCallSnapshot.call_status === 'voicemail'
          || (shownCallSnapshot.call_outcome && ['voicemail', 'left_voicemail', 'no_answer', 'not_reachable', 'busy', 'failed', 'canceled'].includes(shownCallSnapshot.call_outcome))
        );
        // 2026-05-14 — refactor : on ne considere QUE le shown call, jamais
        // chercher un humain dans prevActiveCalls (qui ouvrait une modale
        // pour un AUTRE call que celui affiche, bug Henry Simon / Lara Vogel).
        // Si le shown call est humain → reste sur la modale en mode callEnded.
        // Sinon → ferme tout.
        const reachedHuman = shownCallSnapshot && isHuman(shownCallSnapshot) && !shownIsVoicemail;
        if (reachedHuman) {
          // UNE SEULE modale : on conserve connectedContact (deja enrichi
          // depuis prospectQueue par l'effet "switch CallSheet"). On ajoute
          // juste le flag callEnded=true. Ne pas reouvrir si prev=null
          // (cas voicemail handler qui a deja nulle connectedContact).
          // RUI-P2 : la fiche (selectedProspect) RESTE ouverte pour qualifier.
          // Le setSelectedProspect(null) d'ici datait de CallSheet ; depuis la
          // fusion dans ProspectDetailPanel, il fermait la fiche au raccroché.
          setConnectedContact(prev => {
            if (!prev) return prev; // skip si deja ferme par autre chemin
            return { ...prev, callEnded: true };
          });
          // Refetch le ParallelCall en BDD pour enrichir les champs manquants
          // (prospect_title, linkedin_url, etc) qui peuvent etre vides dans
          // le snapshot Twilio. Async, best-effort. Methode = .get(id) sur
          // l'entityClient (corrige bug 2026-05-14 findById n'existe pas).
          if (shownCallSnapshot.id) {
            api.entities.ParallelCall.get(shownCallSnapshot.id).then((fresh) => {
              if (!fresh) return;
              const fromQueue = prospectQueue.find(p => p.prospect_phone === fresh.prospect_phone);
              const pick = (a, b, c) => (a && String(a).trim() ? a : (b && String(b).trim() ? b : (c || '')));
              setConnectedContact(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  prospect_name:    pick(prev.prospect_name,    fresh.prospect_name,    fromQueue?.prospect_name),
                  prospect_company: pick(prev.prospect_company, fresh.prospect_company, fromQueue?.prospect_company),
                  prospect_title:   pick(prev.prospect_title,   fresh.prospect_title,   fromQueue?.prospect_title),
                  prospect_email:   pick(prev.prospect_email,   fresh.prospect_email,   fromQueue?.prospect_email),
                  linkedin_url:     pick(prev.linkedin_url,     fresh.linkedin_url,     fromQueue?.linkedin_url),
                  website:          pick(prev.website,          fresh.website,          fromQueue?.website),
                };
              });
            }).catch(() => undefined);
          }
        } else {
          // Voicemail / no_answer / busy / failed / non-shown call :
          // aucune modal post-call. On ferme tout residuel.
          setShowCallSheet(false);
          setConnectedContact(null);
        }
      }
    }
    prevActiveCalls.current = activeCalls;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCalls, allCalls, selectedProspect, prospectQueue]);

  // Voicemail auto-hangup (Power Mode) — MIGRE dans callMachine (Phase 4 etape 4b).
  // L'entry action 'hangupVoicemail' de l'etat 'voicemail' fait maintenant le
  // hangup Twilio + POST call_outcome='voicemail'. Le useEffect a ete retire.
  // Voir callPoolPorts.hangupVoicemail dans ce fichier.

  // Auto-close CallSheet sur voicemail — MIGRE dans callMachine (Phase 4 etape 4b).
  // L'etat 'voicemail' de la machine declenche callActions.closeCallSheet + transition
  // 'completed' apres 300ms. CallSheet derive showCallSheet de la machine (etape 3c).

  // ── Reset prospect queue status when calls end ─────────────────────────────
  // dialedPhonesThisSessionRef + previouslyActivePhonesRef + state mirror →
  // DialerSessionContext. ADR-032 — track les telephones dialés dans cette
  // session (anti-boucle pour canCall) via diff sur activeCalls.
  useEffect(() => {
    if (!activeCalls) return;
    const currentPhones = new Set(activeCalls.map(c => c.prospect_phone).filter(Boolean));
    // Phones qui étaient actifs au tick précédent et qui ne le sont plus
    // = appels terminés → à marquer "dialé cette session".
    for (const phone of previouslyActivePhonesRef.current) {
      if (!currentPhones.has(phone)) {
        dialedPhonesThisSessionRef.current.add(phone);
      }
    }
    // Only write when the set grew: a new array on every run re-rendered the
    // provider, and any unstable activeCalls reference then looped (RUI-17).
    setDialedPhonesThisSession(prev =>
      prev.length === dialedPhonesThisSessionRef.current.size ? prev : Array.from(dialedPhonesThisSessionRef.current));
    previouslyActivePhonesRef.current = currentPhones;
  }, [activeCalls]);

  // Enforce single in-line call
  useEffect(() => {
    if (!activeCalls || activeCalls.length <= 1) return;
    if (singleLineEnforceRef.current) return;
    const connected = activeCalls.find(c => c.call_status === 'connected_to_agent');
    let primary = connected || null;
    if (!primary) {
      const humans = activeCalls.filter(c => c.call_status === 'human_detected');
      if (humans.length > 1) primary = humans.sort((a, b) => new Date(a.started_at || a.created_date) - new Date(b.started_at || b.created_date))[0];
    }
    if (!primary) return;
    const toHangup = activeCalls.filter(c => c.id !== primary.id && ['dialing', 'ringing', 'ai_analyzing', 'human_detected', 'connected_to_agent'].includes(c.call_status));
    if (toHangup.length === 0) return;
    singleLineEnforceRef.current = true;
    const nowIso = new Date().toISOString();
    Promise.all(toHangup.map(async (call) => {
      try { if (call.external_call_id) await TwilioCalls.hangupCall(call.external_call_id); } catch (_e) {}
      // Strategie strict-humain 2026-05-13 v3 : pas de 'contacted' cote front
      // (pas d'acces a answeredBy). Si signal humain → on n'ecrit pas
      // call_outcome, le backend tranchera via answeredBy au webhook completed.
      const isVoicemail = call.call_status === 'voicemail' || call.call_outcome === 'voicemail' || call.call_outcome === 'left_voicemail';
      const hasHumanSignal = !isVoicemail && (['connected_to_agent', 'human_detected'].includes(call.call_status) || !!call.connected_at);
      let updates;
      if (isVoicemail) {
        // i18n-exempt: call note stored on the call record, not interface copy
        updates = { call_status: 'completed', call_outcome: 'no_answer', call_notes: `${primary.prospect_name || 'Un autre prospect'} pris en premier (repondeur ici)`, ended_at: nowIso };
      } else if (hasHumanSignal) {
        // On a un signal humain mais on ne peut pas confirmer sans answeredBy.
        // On marque completed sans outcome ; le backend posera 'contacted' ou
        // 'no_answer' au webhook completed selon answeredBy + duree >= 11s.
        // i18n-exempt: call note stored on the call record, not interface copy
        updates = { call_status: 'completed', call_notes: `${primary.prospect_name || 'Un autre prospect'} pris en premier`, ended_at: nowIso };
      } else {
        // i18n-exempt: call note stored on the call record, not interface copy
        updates = { call_status: 'failed', call_outcome: INTERRUPTED_OUTCOME, call_notes: `${primary.prospect_name || 'Un autre prospect'} est en ligne`, ended_at: nowIso };
      }
      try { await api.entities.ParallelCall.update(call.id, updates); } catch (_e) {}
    })).finally(() => { singleLineEnforceRef.current = false; queryClient.invalidateQueries({ queryKey: ['parallel-calls'] }); });
  }, [activeCalls]);

  // Rolling refill (Power Mode) — extrait dans useRollingRefill (Phase 4 etape 7b-3).
  useRollingRefill({
    pendingDialsRef, stopRequestedRef, autoDialTimerRef,
    lastDialDispatchAtRef, dialedPhonesThisSessionRef, singleLineEnforceRef,
    connectedContactRef,
    activeCalls, parallelCallsCount, dialerRunState, stoppingAll, powerMode,
    powerModePauseBetweenBatches, prospectQueue, latestCallByPhone,
    isProspectAtCallLimit, selectedSource, showCallSheet, connectedContact,
    setDialerRunState, setPowerMode,
    startDialingMutation, dialScopeIds,
  });

  // ==========================================================================
  // Context value
  // ==========================================================================

  const value = useMemo(() => ({
    // Auth
    callerData, needsLogin,
    // Twilio device
    deviceRef, deviceReady, deviceState, deviceError, deviceIdentity, micPermissionGranted,
    incomingCallConnection,
    audioInputDevice, setAudioInputDevice, audioOutputDevice, setAudioOutputDevice, availableDevices,
    // Session
    isDialing, dialerRunState, setDialerRunState, stoppingAll,
    isMuted, setIsMuted, isOnHold, setIsOnHold, dialingSessionStartedAt,
    // Data
    prospectQueue, allCalls, activeCalls, teamMembers,
    // ADR-024 phase 2 — Customer-filtered views. Use these in any component
    // that should respect the active Customer selector (dialer queue, call
    // history, follow-ups, meetings, analytics). Use the unfiltered ones
    // (prospectQueue, allCalls) only when the tenant-wide view is wanted.
    filteredProspectQueue, filteredAllCalls,
    prospectsBySource, sources, latestCallByPhone, prospectCallMap, callerIdStats,
    stats,
    // Settings
    language, setLanguage, theme, setTheme, cycleTheme, darkMode, soundEnabled, setSoundEnabled,
    powerMode, setPowerMode, parallelCallsCount, setParallelCallsCount,
    selectedCallerId, setSelectedCallerId, twilioNumbers,
    activeCallerIds, setActiveCallerIds, activeCallerNumbers,
    callerIdRotation, setCallerIdRotation,
    ringTimeout, setRingTimeout,
    maxCallAttempts, setMaxCallAttempts, maxCallAttemptsPeriod, setMaxCallAttemptsPeriod,
    voicemailMode, setVoicemailMode, voicemailAudioUrl, setVoicemailAudioUrl,
    powerModeLeaveVoicemail, setPowerModeLeaveVoicemail,
    powerModePauseBetweenBatches, setPowerModePauseBetweenBatches,
    completeTaskOnDial, setCompleteTaskOnDial,
    kanbanEnabled, setKanbanEnabled, viewMode, setViewMode,
    // Prospect selection
    selectedProspect, setSelectedProspect, selectedProspects, setSelectedProspects,
    selectedSource, setSelectedSource,
    // ADR-024 phase 2 — Customer (B2B client) selection.
    selectedCustomerId, setSelectedCustomerId,
    // Qualification / email
    unqualifiedCalls, setUnqualifiedCalls,
    emailCall, setEmailCall, prefillMeeting, setPrefillMeeting,
    dialScopeIds, setDialScopeIds,
    // Computed
    isProspectAtCallLimit, getNextCallerId,
    // Handlers
    handleStartDialing, handleManualStartDialing, handleResumeDialing, handleStopAll, handleStopDialing, handleHangupAndContinue, handleHangupAndNext, handleHangupSingleCall, handleSkipBatch, handleLogout, handleCallProspect, clearDialedPhonesForRelance, dialedPhonesThisSession, coachingService,
    handleQualifyCall,
    handleTogglePowerHour,
    startDialingMutation, qualifyCallMutation,
    updateProspectQueueWithRetry,
    // Power Hour
    powerHourActive, powerHourCalls, powerHourGoal, setPowerHourGoal,
    stopAfterConnected, setStopAfterConnected, personalCallerIds, setPersonalCallerIds,
    // Smart Queue
    smartQueueEnabled, setSmartQueueEnabled,
    // Gamification
    agentXP, agentLevel, addXP,
    // Import dialog
    importDialogOpen, setImportDialogOpen,
    // V5: CallSheet / OnCallBar
    showCallSheet, setShowCallSheet, connectedContact, setConnectedContact,
    // V5: DTMF
    showKeypad, setShowKeypad, dtmfInput, setDtmfInput,
    // V5: Coach Mode
    coachMode, setCoachMode,
    // V5: Celebration
    showCelebration, setShowCelebration,
    // V5: Notifications
    notifications, setNotifications, showNotifications, setShowNotifications,
    // V5: Nav tabs
    navTabOrder, setNavTabOrder,
    // V5: Column config
    columnConfig, setColumnConfig,
    // V5: CRM
    crmTab, setCrmTab, crmProvider, setCrmProvider,
    hubspotSyncConfig, setHubspotSyncConfig,
    // HubSpot sync toggles (server-persisted)
    hubspotAutoSyncTasks, setHubspotAutoSyncTasks,
    hubspotAutoSyncRecordings, setHubspotAutoSyncRecordings,
    hubspotLastSyncTasks, setHubspotLastSyncTasks,
    hubspotLastSyncRecordings, setHubspotLastSyncRecordings,
    // V5: Queue view
    queueViewMode, setQueueViewMode,
    // V5: Mic
    selectedMicrophone, setSelectedMicrophone,
    // Server-persisted prefs
    territories, setTerritories,
    activeMarkets, setActiveMarkets,
    conversationThreshold, setConversationThreshold,
    conversationOutcomes, setConversationOutcomes,
    powerHourDuration, setPowerHourDuration,
    remindersEnabled, setRemindersEnabled,
    reminderChannels, setReminderChannels,
    aiCoachEnabledPowerMode, setAiCoachEnabledPowerMode,
    aiCoachEnabledManualCall, setAiCoachEnabledManualCall,
  }), [
    callerData, needsLogin,
    deviceRef, deviceReady, deviceState, deviceError, deviceIdentity, micPermissionGranted,
    incomingCallConnection,
    audioInputDevice, audioOutputDevice, availableDevices,
    isDialing, dialerRunState, stoppingAll, isMuted, isOnHold,
    prospectQueue, allCalls, activeCalls, teamMembers,
    prospectsBySource, sources, latestCallByPhone, prospectCallMap, callerIdStats, stats,
    language, theme, darkMode, soundEnabled, powerMode, parallelCallsCount,
    selectedCallerId, twilioNumbers, activeCallerIds, activeCallerNumbers, callerIdRotation, ringTimeout,
    maxCallAttempts, maxCallAttemptsPeriod, voicemailMode, voicemailAudioUrl, powerModeLeaveVoicemail, powerModePauseBetweenBatches, completeTaskOnDial,
    kanbanEnabled, viewMode,
    selectedProspect, selectedProspects, selectedSource,
    unqualifiedCalls, emailCall, prefillMeeting, dialScopeIds,
    isProspectAtCallLimit, getNextCallerId,
    handleStartDialing, handleManualStartDialing, handleResumeDialing, handleStopAll, handleStopDialing, handleHangupAndContinue, handleHangupAndNext, handleSkipBatch, handleLogout, handleCallProspect, dialedPhonesThisSession, coachingService,
    handleQualifyCall, handleTogglePowerHour,
    powerHourActive, powerHourCalls, powerHourGoal, smartQueueEnabled,
    stopAfterConnected, personalCallerIds,
    startDialingMutation, qualifyCallMutation,
    showCallSheet, connectedContact,
    showKeypad, dtmfInput,
    coachMode, showCelebration,
    notifications, showNotifications,
    navTabOrder, columnConfig,
    crmTab, crmProvider, hubspotSyncConfig,
    hubspotAutoSyncTasks, hubspotAutoSyncRecordings,
    hubspotLastSyncTasks, hubspotLastSyncRecordings,
    queueViewMode, selectedMicrophone,
    territories, activeMarkets, conversationThreshold, conversationOutcomes,
    powerHourDuration, remindersEnabled, reminderChannels,
    aiCoachEnabledPowerMode, aiCoachEnabledManualCall,
  ]);

  return <DialerContext.Provider value={value}>{children}</DialerContext.Provider>;
}

/**
 * IncomingCallBridge — composant pont 2026-06-04 (big-bang refactor).
 * Observe `incomingCallConnection` du TwilioDeviceContext et le pousse
 * dans la session d'appel entrant. Independant du reste de DialerContext
 * pour ne pas reintroduire de couplage.
 *
 * Quand l'agent accepte (via session.handleAccept), on ouvre AUSSI la
 * modale ProspectDetailPanel (selectedProspect) sur ce prospet, pour
 * que la mise a jour de l'UI suive le flow attendu.
 */
function IncomingCallBridge() {
  const { incomingCallConnection, setIncomingCallConnection } = useTwilioDevice();
  const session = useIncomingCallSession();
  const { setSelectedProspect } = useDialer();
  const lastSeenSidRef = useRef(null);

  // 2026-06-04 (Fowler audit) — Le hack window.__incomingCallActive a ete
  // supprime. DialerProviderInner consomme maintenant useIncomingCallSession()
  // directement (le Provider est dispo dans la subtree).

  // Pousse l'appel entrant dans la session des qu'il arrive.
  useEffect(() => {
    if (!incomingCallConnection) return;
    const sid = incomingCallConnection.parameters?.CallSid;
    if (!sid || lastSeenSidRef.current === sid) return;
    lastSeenSidRef.current = sid;
    session.handleIncomingReceived(incomingCallConnection);
  }, [incomingCallConnection, session]);

  // Le bandeau d'appel entrant (IncomingCallToast) se ferme des que la
  // session a QUITTE la sonnerie : decroche ('accepted' puis 'live'),
  // refuse ou raccroche ('ended'). Seules 'live' et 'ended' etaient
  // enumerees ici. La phase 'accepted', tenue pendant tout l'aller-retour de
  // creation du ParallelCall, n'etait couverte par aucune des deux : le
  // bandeau continuait d'annoncer "Sonne..." alors que l'agent avait
  // decroche, et il ne se refermait plus du tout si cet aller-retour
  // n'aboutissait pas.
  // Le garde sur le CallSid empeche de fermer le bandeau d'un AUTRE appel
  // entrant arrive pendant celui-ci : la session n'en suit qu'un a la fois,
  // et sa phase ne dit rien du second.
  useEffect(() => {
    if (session.phase === 'idle' || session.phase === 'ringing') return;
    const bannerSid = incomingCallConnection?.parameters?.CallSid;
    if (bannerSid && session.callSid && bannerSid !== session.callSid) return;
    try { setIncomingCallConnection?.(null); } catch (_e) { /* silent */ }
  }, [session.phase, session.callSid, incomingCallConnection, setIncomingCallConnection]);

  // Quand la session passe en 'live' (post-accept), on ouvre la modale
  // ProspectDetailPanel sur ce prospect.
  useEffect(() => {
    if (session.phase !== 'live' || !session.prospect) return;
    try {
      setSelectedProspect({
        id: '',
        prospect_name: session.prospect.name,
        prospect_phone: session.prospect.phone,
        prospect_company: session.prospect.company || '',
      });
    } catch (_e) { /* silent */ }
  }, [session.phase, session.prospect, setSelectedProspect]);

  return null;
}

/**
 * DialerProvider — wrapper qui orchestre tous les sous-providers.
 *
 * 2026-06-04 (Fowler audit 4/3) — Extraction du DialerAuthProvider.
 * Avant, callerData + needsLogin etaient des useState ICI passes en
 * props a DialerProviderInner. Maintenant, ils vivent dans le DialerAuthProvider
 * et DialerProviderInner les consomme via useDialerAuth(). Premier pas
 * vers un decoupage thematique complet du god-context.
 *
 * Sous-providers (ordre d'imbrication = ordre de dependance) :
 *  - DialerAuthProvider : caller authentification
 *  - DialerGamificationProvider : XP + Power Hour (depend de Auth)
 *  - DialerSettingsProvider : settings persistes serveur
 *  - TwilioDeviceProvider : SDK Twilio Voice + incoming detection
 *  - DialerSessionProvider : pool de dialing (refs anti-race)
 *  - IncomingCallSessionProvider : cycle inbound (useReducer pur)
 *  - DialerProviderInner : god-context legacy (en cours de demantelement)
 */
function DialerProviderShell({ children }: { children: React.ReactNode }) {
  // 2026-06-04 (Fowler 6/3) — On consomme callerData ici juste pour le
  // passer aux Providers qui en ont vraiment besoin (DialerSettings +
  // IncomingCallSession). DialerProviderInner le lit directement via
  // useDialerAuth() - plus de prop drilling.
  const { callerData } = useDialerAuth();
  return (
    <DialerOrchestrationProvider>
      <DialerGamificationProvider>
        <DialerTelephonyProvider>
          <DialerSettingsProvider callerData={callerData}>
            <TwilioDeviceProvider>
              <DialerSessionProvider>
                <DialerUIProvider>
                  <IncomingCallSessionProvider callerData={callerData} selectedCustomerId={null}>
                    <DialerDataBridge>
                      <DialerDerivedProvider>
                        <DialerProviderInner>
                          <IncomingCallBridge />
                          {children}
                        </DialerProviderInner>
                      </DialerDerivedProvider>
                    </DialerDataBridge>
                  </IncomingCallSessionProvider>
                </DialerUIProvider>
              </DialerSessionProvider>
            </TwilioDeviceProvider>
          </DialerSettingsProvider>
        </DialerTelephonyProvider>
      </DialerGamificationProvider>
    </DialerOrchestrationProvider>
  );
}

/**
 * DialerDataBridge — pont qui injecte settings + session dans DialerDataProvider.
 *
 * 2026-06-04 (Fowler etape 2/5) — DialerDataProvider a besoin de ringTimeout
 * (settings), dialerRunState et powerMode (session) pour cadencer ses polls.
 * On les passe en props plutot que de creer une dependance circulaire entre
 * les contexts. Le bridge est un composant React minimal — pas un nouveau
 * context.
 */
function DialerDataBridge({ children }: { children: React.ReactNode }) {
  const { ringTimeout, powerMode } = useDialerSettings();
  const { dialerRunState } = useDialerSession();
  // ENG-4: the queue and history poll where they are shown, or during a session.
  const { pathname } = useLocation();
  const displayListsLive = displayListsPoll(pathname, dialerRunState === 'running' || !!powerMode);
  return (
    <DialerDataProvider
      ringTimeout={ringTimeout as number}
      dialerRunState={dialerRunState as string}
      powerMode={powerMode as boolean}
      displayListsLive={displayListsLive}
    >
      {children}
    </DialerDataProvider>
  );
}

export function DialerProvider({ children }) {
  return (
    <DialerAuthProvider>
      <DialerProviderShell>{children}</DialerProviderShell>
    </DialerAuthProvider>
  );
}

export function useDialer(): DialerContextValue {
  const ctx = useContext(DialerContext);
  if (!ctx) throw new Error('useDialer must be used within <DialerProvider>');
  return ctx;
}
